import { Alert } from 'antd';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Logo from '../../Logo';

interface OAuthProvider {
  name: string;
  displayName: string;
  apiEndpoint: string;
  buttonText: string;
  successMessage: string;
  loadingText: string;
  errorMessage: string;
  emailMismatchError: string;
  authPrompt: string;
}

const OAUTH_PROVIDERS: Record<string, OAuthProvider> = {
  lofty: {
    name: 'lofty',
    displayName: 'Lofty',
    apiEndpoint: 'lofty-oauth',
    buttonText: 'Authorize Lofty Access',
    successMessage:
      'Congratulations! HouseWhisper is now authorized to sync with your Lofty account. You are all set—we will take care of the rest and you can close this window.',
    loadingText: 'Loading...',
    errorMessage: 'Failed to load Lofty authorization link.',
    emailMismatchError: 'Lofty login failed: Wrong account',
    authPrompt:
      'To enable HouseWhisper to sync with your Lofty account, please authorize access below.',
  },
  moxiworks: {
    name: 'moxiworks',
    displayName: 'MoxiWorks',
    apiEndpoint: 'moxiworks-oauth',
    buttonText: 'Authorize MoxiWorks Access',
    successMessage:
      'Congratulations! HouseWhisper is now authorized to sync with your MoxiWorks account. You are all set—we will take care of the rest and you can close this window.',
    loadingText: 'Loading...',
    errorMessage: 'Failed to load MoxiWorks authorization link.',
    emailMismatchError: 'MoxiWorks login failed: Wrong account',
    authPrompt:
      'To enable HouseWhisper to sync with your MoxiWorks account, please authorize access below.',
  },
};

export const GenericOAuth = ({
  apiUrl,
  provider,
}: {
  apiUrl: string;
  provider: string;
}): JSX.Element => {
  const { clientId } = useParams();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const error = searchParams.get('error');
  const expectedEmail = searchParams.get('expected_email');
  const actualEmail = searchParams.get('actual_email');
  const accountManager = searchParams.get('account_manager');
  const [oauthLink, setOauthLink] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  const providerConfig = OAUTH_PROVIDERS[provider];

  useEffect(() => {
    if (status === 'success' || !providerConfig) {
      return;
    }
    const fetchOAuthLink = async () => {
      setLoading(true);
      setPageError(null);
      try {
        const url = `${apiUrl}/api/auth/${providerConfig.apiEndpoint}/${clientId}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(
            `Failed to get ${providerConfig.displayName} OAuth URL`,
          );
        }
        const jsonData = await response.json();
        setOauthLink(jsonData.url);
      } catch (err) {
        setPageError(providerConfig.errorMessage);
      } finally {
        setLoading(false);
      }
    };
    void fetchOAuthLink();
  }, [apiUrl, clientId, status, providerConfig]);

  if (!providerConfig) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '20px',
        }}
      >
        <Logo />
        <div style={{ marginTop: '20px' }}>
          <Alert
            description={`OAuth provider "${provider}" is not supported.`}
            message="Invalid Provider"
            showIcon
            type="error"
          />
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '20px',
        }}
      >
        <Logo />
        <div style={{ marginTop: '20px' }}>
          <h1>Congratulations!</h1>
          <p>{providerConfig.successMessage}</p>
        </div>
      </div>
    );
  }

  if (status === 'error' && error === `${providerConfig.name}_email_mismatch`) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '20px',
        }}
      >
        <Logo />
        <div style={{ marginTop: '20px', maxWidth: 500 }}>
          <Alert
            description={
              <div>
                <p>
                  <b>We were expecting a login from:</b>
                  <br />
                  <span style={{ color: '#1976d2' }}>{expectedEmail}</span>
                </p>
                <p>
                  <b>But you logged in as:</b>
                  <br />
                  <span style={{ color: '#d32f2f' }}>{actualEmail}</span>
                </p>
                <p>
                  Please log out of {providerConfig.displayName} and try again
                  with the correct account.
                  <br />
                  {accountManager ? (
                    <>
                      If you need help, contact <b>{accountManager}</b>.
                    </>
                  ) : (
                    <>If you need help, contact support.</>
                  )}
                </p>
              </div>
            }
            message={providerConfig.emailMismatchError}
            showIcon
            type="error"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100vh',
        paddingTop: '100px',
      }}
    >
      <div style={{ width: '100%', textAlign: 'center', marginBottom: '20px' }}>
        <Logo />
      </div>
      <div style={{ width: '90%' }}>
        <div
          style={{
            fontSize: '24px',
            lineHeight: '32px',
            textAlign: 'center',
            fontWeight: '300',
            marginBottom: '32px',
          }}
        >
          {providerConfig.authPrompt}
        </div>
        {pageError && (
          <div
            style={{
              color: '#d32f2f',
              marginBottom: '20px',
              textAlign: 'center',
            }}
          >
            {pageError}
          </div>
        )}
        <div style={{ marginTop: '50px', width: '100%', textAlign: 'center' }}>
          <button
            disabled={loading || !oauthLink}
            onClick={() => {
              if (oauthLink) {
                window.location.href = oauthLink;
              }
            }}
            style={{
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              padding: '16px 32px',
              borderRadius: '4px',
              fontSize: '18px',
              cursor: loading || !oauthLink ? 'not-allowed' : 'pointer',
              opacity: loading || !oauthLink ? 0.6 : 1,
            }}
          >
            {loading ? providerConfig.loadingText : providerConfig.buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenericOAuth;
